import React, { useState, useEffect } from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Table,
  ModalLayout,
  Descriptions,
  Text,
} from 'components';
import {
  Button,
  DatePicker,
  Input,
  Select,
  Tag,
  Empty,
  Checkbox,
  Spin,
} from 'antd';
import moment from 'moment';
import dayjs from 'dayjs';
import Formatter from 'utils/Formatter';

const ADMIN_TYPE = {
  ADMIN: '총 관리자',
  STORE_ADMIN: '매장 관리자',
  SHOP_ADMIN: '쇼핑몰 관리자',
};

const INITIAL_VALUE = {
  admin_level: null,
  admin_privilege: ['C', 'R', 'U', 'D'],
  start: '',
  end: '',
  admin_name: null,
};

const ManagerListPresenter = (props) => {
  /* ===== PROPS ===== */
  const {
    data,
    auth,
    modalData,
    modalType,
    isOpened,
    onOpen,
    onChange,
    onSubmit,
    onDelete,
  } = props; // 데이터가 admin 형식의 JSON 배열로 전달된다고 가정
  // console.log(data);

  const [privilegeList, setPrivilegeList] = useState(['X', 'X', 'X', 'X']); // 초기 권한 값 설정
  const [allPrivilegesSelected, setAllPrivilegesSelected] = useState(false); // 전체 권한 선택 상태 관리

  const [isLoading, setIsLoading] = React.useState(false);
  const [filterList, setFilterList] = React.useState(data);
  const [filterItem, setFilterItem] = React.useState(INITIAL_VALUE);

  useEffect(() => {
    const privilegeString = privilegeList.join('');
    onChange('admin_privilege', privilegeString, 'admin');

    // 모든 권한이 선택되었는지 확인하여 전체 권한 상태 업데이트
    setAllPrivilegesSelected(
      privilegeList.every((privilege) => privilege !== 'X')
    );
    // eslint-disable-next-line
  }, [privilegeList]);

  // 권한 목록 업데이트 useEffect
  useEffect(() => {
    if (modalData?.admin?.admin_privilege) {
      const privilegeString = modalData.admin.admin_privilege;
      const updatedPrivileges = privilegeString
        .split('')
        .map((privilege) =>
          ['C', 'R', 'U', 'D'].includes(privilege) ? privilege : 'X'
        );

      setPrivilegeList(updatedPrivileges);
    }
  }, [modalData?.admin?.admin_privilege]);

  // 특정 권한 추가/삭제 로직
  const handleTogglePrivilege = (privilege) => {
    const privilegeIndex = getPrivilegeIndex(privilege);
    if (privilegeIndex !== -1) {
      setPrivilegeList((prev) => {
        const updatedPrivileges = [...prev];
        updatedPrivileges[privilegeIndex] =
          updatedPrivileges[privilegeIndex] === privilege ? 'X' : privilege;
        return updatedPrivileges;
      });
    }
  };

  // 전체 권한 추가/삭제
  const handleToggleAllPrivileges = () => {
    if (allPrivilegesSelected) {
      // 모든 권한 해제
      setPrivilegeList(['X', 'X', 'X', 'X']);
    } else {
      // 모든 권한 추가
      setPrivilegeList(['C', 'R', 'U', 'D']);
    }
  };

  // 권한에 따른 인덱스 매핑
  const getPrivilegeIndex = (privilege) => {
    const privilegeMap = { C: 0, R: 1, U: 2, D: 3 };
    return privilegeMap[privilege] ?? -1;
  };

  /**
   * 필터 검색
   * --
   * @param {string} type
   */
  const handleSearchList = (type = 'search') => {
    let standard = filterItem;
    if (type === 'reset') {
      standard = INITIAL_VALUE;
      setFilterItem(INITIAL_VALUE);
    }

    const { admin_level, admin_privilege, start, end, admin_name } = standard;

    setIsLoading(true);

    const filtered = data?.filter((_item) => {
      if (admin_level && _item?.admin_level !== admin_level) {
        return false;
      }

      if (admin_privilege && !admin_privilege.includes(_item.admin_privilege)) {
        const privilege = _item.admin_privilege.split('');
        const isExist = privilege.some((d) => admin_privilege.includes(d));

        if (!isExist) return false;
      }

      if (start && _item.created_at <= dayjs(start).unix()) {
        return false;
      }

      if (end && _item.created_at >= dayjs(end).unix()) {
        return false;
      }

      if (admin_name && !_item.admin_name.includes(admin_name)) {
        return false;
      }

      return true;
    });

    setTimeout(() => {
      setFilterList(filtered);
      setIsLoading(false);
    }, 1000);
  };

  /* ===== STYLES ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const columns = [
    { title: '관리자 번호', key: 'admin_id' },
    {
      title: '권한 타입',
      key: 'type',
      render: (type) => (
        <Tag
          style={{
            background: 'none',
            color:
              type === 'ADMIN'
                ? '#37383C'
                : type === 'STORE_ADMIN'
                ? '#58CF04'
                : '#EAA300',
            borderColor:
              type === 'ADMIN'
                ? '#37383C'
                : type === 'STORE_ADMIN'
                ? '#58CF04'
                : '#EAA300',
            fontSize: 10,
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {ADMIN_TYPE[type]}
        </Tag>
      ),
      width: 100,
    },
    {
      title: '등록일',
      key: 'created_at',
      render: (created_at) =>
        moment.unix(created_at).format('YYYY-MM-DD HH:mm:ss'),
    },
    { title: '관리자 아이디', key: 'admin_account' },
    { title: '직책', key: 'admin_position' },
    { title: '관리자 명', key: 'admin_name' },
    { title: '이메일', key: 'admin_email' },
    { title: '연락처', key: 'admin_phone' },
    {
      title: '비고',
      key: '비고',
      render: (_, record) => (
        <>
          {auth[2] === 'U' && (
            <Button
              size="small"
              style={{
                borderColor: 'var(--primary-color)',
                color: 'var(--primary-color)',
                marginRight: 4,
              }}
              onClick={() => {
                onOpen(isOpened, 'admin', {
                  ...record,
                  created_at: record?.created_at
                    ? Formatter.unixToDate(record?.created_at, 'YYYY-MM-DD')
                    : null,
                  updated_at: record?.updated_at
                    ? Formatter.unixToDate(record?.updated_at, 'YYYY-MM-DD')
                    : null,
                });
              }}
            >
              수정
            </Button>
          )}
          {/* self가 'Y'인 경우 삭제 버튼을 숨김 */}
          {record.self !== 'Y' && auth[3] === 'D' && (
            <Button
              size="small"
              style={{ borderColor: '#EE4700', color: '#EE4700' }}
              onClick={() => {
                window.confirm(
                  `정말 ${record?.admin_name} 관리자을(를) 삭제하시겠습니까?`
                ) && onDelete(record?.admin_id);
              }}
            >
              삭제
            </Button>
          )}
        </>
      ),
    },
  ];

  const formItem = [
    {
      label: '권한 레벨',
      form: [
        {
          input: (
            <Select
              size="large"
              value={filterItem.admin_level}
              options={[
                { label: '전체', value: null },
                { label: '최고 레벨', value: 1 },
                { label: '중간 레벨', value: 2 },
                { label: '하위 레벨', value: 3 },
              ]}
              style={{ ...styles.form.input.large }}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  admin_level: e,
                }))
              }
            />
          ),
        },
      ],
    },
    {
      label: '관리자 권한',
      form: [
        {
          input: (
            <Checkbox.Group
              options={[
                { label: '생성', value: 'C' },
                { label: '읽기', value: 'R' },
                { label: '수정', value: 'U' },
                { label: '삭제', value: 'D' },
              ].map((item) => ({
                label: item.label,
                value: item.value,
              }))}
              value={filterItem.admin_privilege}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  admin_privilege: e,
                }))
              }
            />
          ),
        },
      ],
    },
    {
      label: '등록일자',
      form: [
        {
          input: (
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() =>
                setFilterItem((prev) => ({
                  ...prev,
                  start: dayjs().startOf('day').format('YYYY-MM-DD'),
                  end: dayjs().endOf('day').format('YYYY-MM-DD'),
                }))
              }
            >
              오늘
            </Button>
          ),
        },
        {
          input: (
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() =>
                setFilterItem((prev) => ({
                  ...prev,
                  start: dayjs()
                    .subtract(1, 'week')
                    .startOf('day')
                    .format('YYYY-MM-DD'),
                  end: dayjs().endOf('day').format('YYYY-MM-DD'),
                }))
              }
            >
              1주일
            </Button>
          ),
        },
        {
          input: (
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() =>
                setFilterItem((prev) => ({
                  ...prev,
                  start: dayjs()
                    .subtract(1, 'month')
                    .startOf('day')
                    .format('YYYY-MM-DD'),
                  end: dayjs().endOf('day').format('YYYY-MM-DD'),
                }))
              }
            >
              1개월
            </Button>
          ),
        },
        {
          input: (
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() =>
                setFilterItem((prev) => ({
                  ...prev,
                  start: dayjs()
                    .subtract(3, 'month')
                    .startOf('day')
                    .format('YYYY-MM-DD'),
                  end: dayjs().endOf('day').format('YYYY-MM-DD'),
                }))
              }
            >
              3개월
            </Button>
          ),
        },
        {
          input: (
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() =>
                setFilterItem((prev) => ({
                  ...prev,
                  start: '',
                  end: '',
                }))
              }
            >
              전체
            </Button>
          ),
        },
        {
          input: (
            <>
              <DatePicker
                size="large"
                format={'YYYY.MM.DD'}
                placeholder=""
                style={{ ...styles.form.date }}
                value={filterItem?.start ? dayjs(filterItem?.start) : ''}
                onChange={(d) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    start: dayjs().startOf('day').format('YYYY-MM-DD'),
                  }))
                }
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <DatePicker
                size="large"
                format={'YYYY-MM-DD'}
                placeholder=""
                style={{ ...styles.form.date }}
                value={filterItem?.end ? dayjs(filterItem?.end) : ''}
                onChange={(d) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    end: dayjs().endOf('day').format('YYYY-MM-DD'),
                  }))
                }
              />
            </>
          ),
        },
      ],
    },
    {
      label: '관리자 명',
      form: [
        {
          input: (
            <Input
              size="large"
              style={{ ...styles.form.input.large }}
              placeholder="관리자 명을 입력해주세요"
              value={filterItem?.admin_name}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  admin_name: e.target.value,
                }))
              }
            />
          ),
        },
      ],
    },
  ];

  React.useEffect(() => {
    if (data && data.length) {
      setFilterList(data);
    }
  }, [data]);

  return auth[1] !== 'R' ? (
    <Empty
      style={{
        height: 500,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
      description={
        <>
          <Title align={'center'}>
            해당 화면에 접근할 수 있는 권한이 없습니다.
          </Title>
        </>
      }
    ></Empty>
  ) : (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={24}>
              <Title style={{ ...styles.title }}>관리자 목록</Title>

              {/* 검색필터 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 40 }}
              >
                <ExpandableContainer borderRadius={4} padding={20}>
                  {formItem.map((_item, _index) => (
                    <div
                      key={`form_${_index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 0px',
                      }}
                    >
                      <div
                        style={{ minWidth: 120, fontSize: 13, fontWeight: 400 }}
                      >
                        <label>{_item.label}</label>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {_item.form.map((_subitem, _subkey) => (
                          <React.Fragment key={`input_${_subkey}`}>
                            {_subitem.input}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </ExpandableContainer>

                {/* 검색/초기화 버튼 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 48,
                  }}
                >
                  <Button
                    size="large"
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => handleSearchList()}
                  >
                    검색
                  </Button>
                  <Button
                    size="large"
                    style={{ ...styles.button }}
                    onClick={() => {
                      handleSearchList('reset');
                    }}
                  >
                    초기화
                  </Button>
                </div>
              </Content>

              {/* 검색결과 */}
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>
                  검색 결과 총 <span>{filterList?.length}</span>건
                </h4>
                <Table
                  data={filterList}
                  columns={columns}
                  minHeight={400}
                  minWidth={1000}
                  bordered
                  hovered
                />
              </Content>
            </Col>
          </Row>
        </Content>
      </Spin>

      <ModalLayout
        type="modal"
        centered
        title={'관리자 정보'}
        open={isOpened && modalType === 'admin'}
        onCancel={() => onOpen(isOpened)}
        closable
        width={'70%'}
        footer={
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Button onClick={() => onOpen(isOpened)}>닫기</Button>
            <Button type="primary" onClick={() => onSubmit('admin', 'UPDATE')}>
              저장
            </Button>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'30px 0px'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <Descriptions
                  items={[
                    {
                      key: 'type',
                      label: '관리자 타입',
                      children: (
                        <Text>{ADMIN_TYPE[modalData?.admin?.type]}</Text>
                        // <Select
                        //   size="large"
                        //   style={{ width: '70%' }}
                        //   placeholder="관리자 타입을 선택하세요."
                        //   options={[
                        //     { label: '총 관리자', value: 'ADMIN' },
                        //     { label: '쇼핑몰 관리자', value: 'SHOP_ADMIN' },
                        //     { label: '매장 관리자', value: 'STORE_ADMIN' },
                        //   ]}
                        //   value={modalData?.admin?.type}
                        //   onChange={(value) => onChange('type', value, 'admin')}
                        // />
                      ),
                    },
                    {
                      key: 'admin_level',
                      label: '관리자 등급',
                      children: (
                        <Select
                          size="large"
                          style={{ width: '100px' }}
                          placeholder="등급을 선택하세요."
                          options={[
                            { label: '1', value: 1 },
                            { label: '2', value: 2 },
                            { label: '3', value: 3 },
                          ]}
                          value={modalData?.admin?.admin_level}
                          onChange={(value) =>
                            onChange('admin_level', value, 'admin')
                          }
                        />
                      ),
                    },
                    {
                      key: 'admin_privilege',
                      label: '권한 설정',
                      children: (
                        <div>
                          {/* 전체 권한 선택 버튼 */}
                          <Button
                            size="large"
                            onClick={handleToggleAllPrivileges}
                            style={{
                              margin: '0 4px',
                              background: allPrivilegesSelected
                                ? '#1677ff'
                                : '#f5f5f5',
                              color: allPrivilegesSelected
                                ? '#ffffff'
                                : '#000000',
                            }}
                          >
                            {allPrivilegesSelected ? '전체 해제' : '전체 선택'}
                          </Button>

                          {/* 권한 개별 선택 버튼들 */}
                          <Button
                            size="large"
                            onClick={() => handleTogglePrivilege('C')}
                            style={{
                              margin: '0 4px',
                              background:
                                privilegeList[0] === 'C'
                                  ? '#1677ff'
                                  : '#f5f5f5',
                              color:
                                privilegeList[0] === 'C'
                                  ? '#ffffff'
                                  : '#000000',
                            }}
                          >
                            등록 (C)
                          </Button>
                          <Button
                            size="large"
                            onClick={() => handleTogglePrivilege('R')}
                            style={{
                              margin: '0 4px',
                              background:
                                privilegeList[1] === 'R'
                                  ? '#1677ff'
                                  : '#f5f5f5',
                              color:
                                privilegeList[1] === 'R'
                                  ? '#ffffff'
                                  : '#000000',
                            }}
                          >
                            읽기 (R)
                          </Button>
                          <Button
                            size="large"
                            onClick={() => handleTogglePrivilege('U')}
                            style={{
                              margin: '0 4px',
                              background:
                                privilegeList[2] === 'U'
                                  ? '#1677ff'
                                  : '#f5f5f5',
                              color:
                                privilegeList[2] === 'U'
                                  ? '#ffffff'
                                  : '#000000',
                            }}
                          >
                            수정 (U)
                          </Button>
                          <Button
                            size="large"
                            onClick={() => handleTogglePrivilege('D')}
                            style={{
                              margin: '0 14px 0 4px ',
                              background:
                                privilegeList[3] === 'D'
                                  ? '#1677ff'
                                  : '#f5f5f5',
                              color:
                                privilegeList[3] === 'D'
                                  ? '#ffffff'
                                  : '#000000',
                            }}
                          >
                            삭제 (D)
                          </Button>
                          {/* 선택된 권한 리스트 출력 */}
                          {privilegeList.map(
                            (privilege, index) =>
                              privilege !== 'X' && (
                                <Tag
                                  key={index}
                                  color="blue"
                                  closable
                                  onClose={() =>
                                    handleTogglePrivilege(privilege)
                                  }
                                >
                                  {privilege}
                                </Tag>
                              )
                          )}
                        </div>
                      ),
                      span: 2,
                    },
                    {
                      key: 'admin_name',
                      label: '관리자 명',
                      children: (
                        <Input
                          size="large"
                          placeholder="이름을 입력하세요."
                          value={modalData?.admin?.admin_name}
                          onChange={(e) =>
                            onChange('admin_name', e.target.value, 'admin')
                          }
                        />
                      ),
                    },
                    {
                      key: 'account_status',
                      label: '계정 상태',
                      children: (
                        <Select
                          size="large"
                          style={{ width: '100px' }}
                          placeholder="계정 상태를 선택하세요."
                          options={[
                            { label: '활성', value: 'ACTIVE' },
                            { label: '비활성', value: 'INACTIVE' },
                          ]}
                          value={modalData?.admin?.account_status}
                          onChange={(value) =>
                            onChange('account_status', value, 'admin')
                          }
                        />
                      ),
                    },
                    {
                      key: 'admin_email',
                      label: '이메일',
                      children: (
                        <Input
                          size="large"
                          placeholder="이메일을 입력하세요."
                          value={modalData?.admin?.admin_email}
                          onChange={(e) =>
                            onChange('admin_email', e.target.value, 'admin')
                          }
                        />
                      ),
                    },
                    {
                      key: 'admin_phone',
                      label: '전화번호',
                      children: (
                        <Input
                          size="large"
                          placeholder="전화번호를 입력하세요."
                          value={modalData?.admin?.admin_phone}
                          onChange={(e) =>
                            onChange(
                              'admin_phone',
                              Formatter.hyphen(e.target.value),
                              'admin'
                            )
                          }
                        />
                      ),
                    },
                    {
                      key: 'created_at',
                      label: '등록일',
                      children: (
                        <DatePicker
                          size="large"
                          value={
                            modalData?.admin?.created_at &&
                            dayjs(modalData?.admin?.created_at)
                          }
                          style={{ width: '100%' }}
                          onChange={(date) =>
                            onChange('created_at', date, 'admin')
                          }
                          disabled
                        />
                      ),
                    },
                    // {
                    //   key: 'updated_at',
                    //   label: '수정일',
                    //   children: (
                    //     <DatePicker
                    //       size="large"
                    //       value={
                    //         modalData?.admin?.updated_at &&
                    //         dayjs(modalData?.admin?.updated_at)
                    //       }
                    //       style={{ width: '100%' }}
                    //       onChange={(date) =>
                    //         onChange('updated_at', date, 'admin')
                    //       }
                    //     />
                    //   ),
                    // },
                    {
                      key: 'admin_position',
                      label: '관리자 직책',
                      children: (
                        <Input
                          size="large"
                          placeholder="직책을 입력하세요."
                          value={modalData?.admin?.admin_position || ''}
                          onChange={(e) =>
                            onChange('admin_position', e.target.value, 'admin')
                          }
                        />
                      ),
                    },
                  ]}
                />
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
    </>
  );
};

export default ManagerListPresenter;
